/**
 * Sessions + diagnostics view — Phase 2.3.h.
 *
 * Owns five related sub-tabs that all share a "session" lens onto the
 * hotspot platform:
 *   - **AP Sessions** — live RADIUS sessions table with view/terminate/
 *     purge actions (sub-tab of Access Points).
 *   - **Grace Sessions** — devices currently in the grace-window state
 *     with their attempt counts and recent session history.
 *   - **AuthMon stats** — per-AP queue depth (ready/pending) on the
 *     authmon side.
 *   - **Mobile Sessions** — issued mobile API keys with revoke action.
 *   - **Hotspot Test (E2E)** — internal tool that drives the captive
 *     portal end-to-end with a synthetic FAS payload.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `openDialog`, `showFlash` — generic.
 *
 * Boot contract: `admin.html` loads this script with the other view
 * modules. The IIFE registers `window.adminViews.sessions`. Mount wires
 * all the listeners. The view-router calls
 * `adminViews.sessions.loadMobileSessions()` for the mobile-sessions
 * tab. The Access Points "sessions" sub-tab in admin.js calls
 * `adminViews.sessions.loadApSessions()`/`loadGraceSessions()`/
 * `loadAuthmonStats()`.
 */

// Phase 3.2.c: replaced `configure({helpers})` injection with direct imports.
import { escapeHtml, formatDateTimeFull } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const adminAPI = api;
const API = api.routes;
const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let apSessionsTbody, apSessionSearch,
      graceSessionsTbody, graceSessionSummary,
      authmonTbody,
      mobileSessionsTbody, mobileSessionSearch,
      hotspotTestStartBtn, hotspotTestNewMacBtn, hotspotTestReloadBtn,
      hotspotTestFrame, hotspotTestStatus, hotspotTestMac,
      hotspotTestGateway, hotspotTestFlow, hotspotTestAndroidVer;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    apSessionsTbody = document.getElementById('apSessionsTbody');
    apSessionSearch = document.getElementById('apSessionSearch');
    graceSessionsTbody = document.getElementById('graceSessionsTbody');
    graceSessionSummary = document.getElementById('graceSessionSummary');
    authmonTbody = document.getElementById('authmonTbody');
    mobileSessionsTbody = document.getElementById('mobileSessionsTbody');
    mobileSessionSearch = document.getElementById('mobileSessionSearch');
    hotspotTestStartBtn = document.getElementById('hotspotTestStartBtn');
    hotspotTestNewMacBtn = document.getElementById('hotspotTestNewMacBtn');
    hotspotTestReloadBtn = document.getElementById('hotspotTestReloadBtn');
    hotspotTestFrame = document.getElementById('hotspotTestFrame');
    hotspotTestStatus = document.getElementById('hotspotTestStatus');
    hotspotTestMac = document.getElementById('hotspotTestMac');
    hotspotTestGateway = document.getElementById('hotspotTestGateway');
    hotspotTestFlow = document.getElementById('hotspotTestFlow');
    hotspotTestAndroidVer = document.getElementById('hotspotTestAndroidVer');
  }

  // ---- State ----------------------------------------------------------
  let apSessionQuery = '';
  let graceSessionsQuery = '';
  let mobileSessionQuery = '';
  let hotspotE2eToken = '';
  let hotspotE2eTokenExp = 0;

  // ---- AP Sessions ----------------------------------------------------
  function renderAPSessions(items = []) {
    apSessionsTbody.innerHTML = '';
    if (!items.length) {
      apSessionsTbody.innerHTML = '<tr><td colspan="9" style="color:var(--sub);">No sessions</td></tr>';
      return;
    }
    items.forEach((s, idx) => {
      const tr = document.createElement('tr');
      const start = s.start_time ? formatDateTimeFull(s.start_time) : '';
      const st = String(s.session_type || '').toLowerCase();
      const badge = st === 'grace'
        ? '<span class="badge off">grace</span>'
        : '<span class="badge ok">regular</span>';
      tr.innerHTML = `
        <td>${idx + 1}</td>
        <td>${escapeHtml(s.mac || '')}</td>
        <td>${badge}</td>
        <td>${escapeHtml(s.ap || '')}</td>
        <td>${escapeHtml(s.ap_ip || '')}</td>
        <td>${escapeHtml(start)}</td>
        <td>${Number(s.timeout || 0)}s</td>
        <td>${Number(s.remaining_sec || 0)}s</td>
        <td>
          <button class="btn" data-act="view" data-mac="${s.mac}">🔎 View</button>
          <button class="btn danger" data-act="terminate" data-mac="${s.mac}">⛔ Terminate</button>
          <button class="btn" data-act="purge" data-mac="${s.mac}">🧹 Purge</button>
        </td>`;
      apSessionsTbody.appendChild(tr);
    });
  }

  async function loadAPSessions() {
    try {
      const data = await adminAPI.fetch(API.sessions(apSessionQuery), { method: 'GET' });
      renderAPSessions(data.items || []);
    } catch (err) {
      renderAPSessions([]); showFlash(err.message, 'error');
    }
  }

  // ---- Hotspot Test (E2E) ---------------------------------------------
  function randomMac() {
    const b = () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0').toUpperCase();
    return `02:${b()}:${b()}:${b()}:${b()}:${b()}`;
  }

  function decodeE2eTokenExp(token) {
    try {
      const payload = String(token || '').split('.', 1)[0] || '';
      const pad = '='.repeat((4 - (payload.length % 4)) % 4);
      const json = atob((payload + pad).replace(/-/g, '+').replace(/_/g, '/'));
      const data = JSON.parse(json);
      return Number(data && data.exp ? data.exp : 0);
    } catch (_) {
      return 0;
    }
  }

  async function getHotspotE2eToken(forceRefresh) {
    const nowSec = Math.floor(Date.now() / 1000);
    if (!forceRefresh && hotspotE2eToken && hotspotE2eTokenExp && nowSec < (hotspotE2eTokenExp - 10)) {
      return hotspotE2eToken;
    }
    const data = await adminAPI.fetch('/api/admin/hotspot/e2e-token', { method: 'GET' });
    const token = data && data.token ? String(data.token) : '';
    if (!token) throw new Error('No e2e token returned');
    hotspotE2eToken = token;
    hotspotE2eTokenExp = decodeE2eTokenExp(token);
    return hotspotE2eToken;
  }

  async function startHotspotE2eTest() {
    if (!hotspotTestFrame || !hotspotTestStatus) { console.warn('sessions: hotspot test elements missing.'); return; }
    hotspotTestStatus.textContent = 'Requesting token…';
    try {
      let token = await getHotspotE2eToken(true);
      const mac = (hotspotTestMac && hotspotTestMac.value ? hotspotTestMac.value : '').trim() || randomMac();
      if (hotspotTestMac) hotspotTestMac.value = mac;
      const gateway = (hotspotTestGateway && hotspotTestGateway.value ? hotspotTestGateway.value : '').trim()
        || 'E2E-AP';
      const gatewayMac = '11:22:33:44:55:66';
      const gatewayAddress = '10.0.0.5';
      const clientIp = '10.0.0.2';
      const authDir = 'fas';
      const fasPlain = `clientmac=${mac},clientip=${clientIp},gatewayname=${gateway},gatewayaddress=${gatewayAddress},authdir=${authDir},gatewaymac=${gatewayMac}`;
      const fas = btoa(unescape(encodeURIComponent(fasPlain)));
      const url = new URL('/index.html', window.location.origin);
      url.searchParams.set('fas', fas);
      url.searchParams.set('client_token', token);
      url.searchParams.set('e2e', '1');
      if (hotspotTestFlow && hotspotTestFlow.value === 'android') {
        url.searchParams.set('skip_grace', '1');
        url.searchParams.set('flow', 'android');
        if (hotspotTestAndroidVer && hotspotTestAndroidVer.value) {
          url.searchParams.set('android_ver', hotspotTestAndroidVer.value);
        }
      }
      hotspotTestFrame.src = url.pathname + url.search;
      const flowLabel = hotspotTestFlow && hotspotTestFlow.value === 'android' ? 'android' : 'ios';
      const verLabel = flowLabel === 'android' && hotspotTestAndroidVer && hotspotTestAndroidVer.value
        ? ` ${hotspotTestAndroidVer.value}`
        : '';
      hotspotTestStatus.textContent = `Running… (${mac} @ ${gateway} · ${flowLabel}${verLabel})`;
    } catch (err) {
      const msg = String(err && err.message ? err.message : err || '');
      if (msg.includes('Invalid test token') || msg.includes('403')) {
        try {
          const token = await getHotspotE2eToken(true);
          const mac = (hotspotTestMac && hotspotTestMac.value ? hotspotTestMac.value : '').trim() || randomMac();
          if (hotspotTestMac) hotspotTestMac.value = mac;
          const gateway = (hotspotTestGateway && hotspotTestGateway.value ? hotspotTestGateway.value : '').trim() || 'E2E-AP';
          const gatewayMac = '11:22:33:44:55:66';
          const gatewayAddress = '10.0.0.5';
          const clientIp = '10.0.0.2';
          const authDir = 'fas';
          const fasPlain = `clientmac=${mac},clientip=${clientIp},gatewayname=${gateway},gatewayaddress=${gatewayAddress},authdir=${authDir},gatewaymac=${gatewayMac}`;
          const fas = btoa(unescape(encodeURIComponent(fasPlain)));
          const url = new URL('/index.html', window.location.origin);
          url.searchParams.set('fas', fas);
          url.searchParams.set('client_token', token);
          url.searchParams.set('e2e', '1');
          if (hotspotTestFlow && hotspotTestFlow.value === 'android') {
            url.searchParams.set('skip_grace', '1');
            url.searchParams.set('flow', 'android');
            if (hotspotTestAndroidVer && hotspotTestAndroidVer.value) {
              url.searchParams.set('android_ver', hotspotTestAndroidVer.value);
            }
          }
          hotspotTestFrame.src = url.pathname + url.search;
          const flowLabel = hotspotTestFlow && hotspotTestFlow.value === 'android' ? 'android' : 'ios';
          const verLabel = flowLabel === 'android' && hotspotTestAndroidVer && hotspotTestAndroidVer.value
            ? ` ${hotspotTestAndroidVer.value}`
            : '';
          hotspotTestStatus.textContent = `Running… (${mac} @ ${gateway} · ${flowLabel}${verLabel})`;
          return;
        } catch (_) {
          // fall through
        }
      }
      hotspotTestStatus.textContent = 'Failed';
      showFlash(err.message, 'error');
    }
  }

  // ---- Grace Sessions -------------------------------------------------
  const formatIsoLocal = (value) => {
    if (!value) return '';
    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? '' : formatDateTimeFull(value);
  };

  function renderGraceSessions(items = [], summary = {}) {
    if (!graceSessionsTbody) return;
    graceSessionsTbody.innerHTML = '';
    const totalDevices = typeof summary.total === 'number' ? summary.total : items.length;
    const activeDevices = typeof summary.active === 'number'
      ? summary.active
      : items.filter(item => item && item.active_session).length;
    if (graceSessionSummary) {
      graceSessionSummary.textContent = `Devices: ${totalDevices || 0} · Active: ${activeDevices || 0}`;
    }

    if (!items.length) {
      graceSessionsTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No grace activity</td></tr>';
      return;
    }

    items.forEach((item, idx) => {
      const tr = document.createElement('tr');
      const mac = item && item.mac ? item.mac : '';
      const contactParts = [];
      if (item && item.phone_number) contactParts.push(item.phone_number);
      if (item && item.customer_name) contactParts.push(item.customer_name);
      const contact = contactParts.length ? contactParts.join(' · ') : '—';
      const attempts = Number(item && item.grace_attempt_count != null ? item.grace_attempt_count : 0);
      const lastGrace = formatIsoLocal(item && item.last_grace_at);
      const lastAp = item && item.last_seen_ap ? item.last_seen_ap : '—';

      let activeHtml = '—';
      if (item && item.active_session) {
        const active = item.active_session;
        const until = formatIsoLocal(active.valid_until);
        const apLabel = active.ap_name ? `@${active.ap_name}` : '';
        const title = (`Active ${apLabel}`).trim() || 'Active';
        activeHtml = escapeHtml(title);
        if (until) {
          activeHtml += `<br><small>${escapeHtml(until)}</small>`;
        }
      }

      const recentSessions = Array.isArray(item && item.recent_sessions ? item.recent_sessions : [])
        .map(sess => {
          const status = sess.status || '—';
          const ap = sess.ap_name ? `@${sess.ap_name}` : '';
          const started = formatIsoLocal(sess.start_time);
          const ended = sess.ended_at ? formatIsoLocal(sess.ended_at) : null;
          const until = sess.valid_until ? formatIsoLocal(sess.valid_until) : null;
          const suffix = ended ? `ended ${ended}` : (until ? `until ${until}` : '');
          const meta = [started, suffix].filter(Boolean).join(' · ');
          return `${escapeHtml(`${status} ${ap}`.trim())}${meta ? `<br><small>${escapeHtml(meta)}</small>` : ''}`;
        })
        .join('<br>');

      tr.innerHTML = `
        <td>${idx + 1 + (summary.offset || 0)}</td>
        <td>${escapeHtml(mac)}</td>
        <td>${escapeHtml(contact)}</td>
        <td>${attempts}</td>
        <td>${escapeHtml(lastGrace || '—')}</td>
        <td>${escapeHtml(lastAp)}</td>
        <td>${activeHtml}</td>
        <td>${recentSessions || '—'}</td>`;
      graceSessionsTbody.appendChild(tr);
    });
  }

  async function loadGraceSessions() {
    if (!graceSessionsTbody) { console.warn('sessions: #graceSessionsTbody missing.'); return; }
    try {
      const data = await adminAPI.fetch(API.adminGraceSessions(graceSessionsQuery), { method: 'GET' });
      renderGraceSessions(data.items || [], data || {});
    } catch (err) {
      renderGraceSessions([], {});
      if (err && err.message) showFlash(err.message, 'error');
    }
  }

  // ---- AuthMon stats --------------------------------------------------
  function renderAuthmonStats(items = []) {
    if (!authmonTbody) return;
    authmonTbody.innerHTML = '';
    if (!items.length) {
      authmonTbody.innerHTML = '<tr><td colspan="4" style="color:var(--sub);">No APs</td></tr>';
      return;
    }
    items.forEach(row => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${escapeHtml(row.gatewayhash || '')}</td>
        <td>${escapeHtml(row.ip || '')}</td>
        <td>${Number(row.ready || 0)}</td>
        <td>${Number(row.pending || 0)}</td>`;
      authmonTbody.appendChild(tr);
    });
  }

  async function loadAuthmonStats() {
    try {
      const data = await adminAPI.fetch(API.adminAuthmonAps, { method: 'GET' });
      renderAuthmonStats(Array.isArray(data) ? data : []);
    } catch (err) {
      renderAuthmonStats([]);
    }
  }

  // ---- Mobile Sessions ------------------------------------------------
  function renderMobileSessions(items = []) {
    mobileSessionsTbody.innerHTML = '';
    if (!items.length) {
      mobileSessionsTbody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">No sessions</td></tr>';
      return;
    }
    items.forEach((s, idx) => {
      const tr = document.createElement('tr');
      const created = s.created_at ? formatDateTimeFull(s.created_at) : '';
      const lastUsed = s.last_used_at ? formatDateTimeFull(s.last_used_at) : '';
      const phone = (s.profile && s.profile.phone_number) || '';
      const name = (s.profile && s.profile.name) || '';
      tr.innerHTML = `
        <td>${idx + 1}</td>
        <td>${escapeHtml(phone)}</td>
        <td>${escapeHtml(name)}</td>
        <td>${escapeHtml(created)}</td>
        <td>${escapeHtml(lastUsed)}</td>
        <td>
          <button class=\"btn danger\" data-act=\"revoke\" data-keyid=\"${s.key_id}\">🗑 Revoke</button>
        </td>`;
      mobileSessionsTbody.appendChild(tr);
    });
  }

  async function loadMobileSessions() {
    try {
      const data = await adminAPI.fetch(API.adminMobileSessions(mobileSessionQuery), { method: 'GET' });
      renderMobileSessions(data.items || []);
    } catch (err) {
      renderMobileSessions([]); showFlash(err.message, 'error');
    }
  }

  let mounted = false;
  function mount() {
    if (mounted) return;
    mounted = true;
    resolveDom();

    if (apSessionsTbody) {
      apSessionsTbody.addEventListener('click', async (e) => {
        const viewBtn = e.target.closest('button[data-act="view"]');
        const termBtn = e.target.closest('button[data-act="terminate"]');
        const purgeBtn = e.target.closest('button[data-act="purge"]');
        if (viewBtn) {
          const mac = viewBtn.dataset.mac;
          if (!mac) return;
          try {
            const data = await adminAPI.fetch(`/api/admin/authmon/device/${encodeURIComponent(mac)}`, { method: 'GET' });
            const el = document.getElementById('authmonDeviceDetail');
            el.textContent = JSON.stringify(data, null, 2);
            openDialog(document.getElementById('authmonDeviceDlg'));
          } catch (err) { showFlash(err.message, 'error'); }
          return;
        }
        if (termBtn) {
          const mac = termBtn.dataset.mac;
          if (!mac) return;
          if (!confirm(`Terminate session for ${mac}?`)) return;
          try {
            await adminAPI.fetch(API.sessionTerminate(mac), { method: 'POST' });
            showFlash('Session terminated');
            loadAPSessions();
            loadAuthmonStats();
          } catch (err) { showFlash(err.message, 'error'); }
          return;
        }
        if (purgeBtn) {
          const mac = purgeBtn.dataset.mac;
          if (!mac) return;
          if (!confirm(`Purge ${mac} from AuthMon queues?`)) return;
          try {
            await adminAPI.fetch(API.adminAuthmonPurge, { method: 'POST', body: JSON.stringify({ mac }) });
            showFlash('Purged from queues');
            loadAuthmonStats();
          } catch (err) { showFlash(err.message, 'error'); }
          return;
        }
      });
    }

    if (apSessionSearch) {
      apSessionSearch.addEventListener('input', (e) => {
        apSessionQuery = e.target.value.trim();
        graceSessionsQuery = apSessionQuery;
        loadAPSessions();
        loadGraceSessions();
      });
    }

    if (mobileSessionsTbody) {
      mobileSessionsTbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-act="revoke"]');
        if (!btn) return;
        const keyId = btn.dataset.keyid;
        if (!keyId) return;
        if (!confirm('Revoke this mobile session?')) return;
        try {
          await adminAPI.fetch(API.adminMobileRevoke(keyId), { method: 'POST' });
          showFlash('Mobile session revoked');
          loadMobileSessions();
        } catch (err) { showFlash(err.message, 'error'); }
      });
    }

    if (mobileSessionSearch) {
      mobileSessionSearch.addEventListener('input', (e) => { mobileSessionQuery = e.target.value.trim(); loadMobileSessions(); });
    }

    if (hotspotTestStartBtn) hotspotTestStartBtn.addEventListener('click', startHotspotE2eTest);
    if (hotspotTestNewMacBtn) hotspotTestNewMacBtn.addEventListener('click', () => {
      const mac = randomMac();
      if (hotspotTestMac) hotspotTestMac.value = mac;
      if (hotspotTestStatus) hotspotTestStatus.textContent = `MAC set to ${mac}`;
    });
    if (hotspotTestReloadBtn) hotspotTestReloadBtn.addEventListener('click', () => {
      if (hotspotTestFrame && hotspotTestFrame.src) hotspotTestFrame.src = hotspotTestFrame.src;
    });

    if (hotspotTestFrame && hotspotTestStatus) {
      hotspotTestFrame.addEventListener('load', () => {
        try {
          const path = hotspotTestFrame.contentWindow && hotspotTestFrame.contentWindow.location
            ? hotspotTestFrame.contentWindow.location.pathname
            : '';
          if (path === '/enjoy_wifi.html') {
            const mac = hotspotTestMac ? String(hotspotTestMac.value || '').trim() : '';
            hotspotTestStatus.textContent = `Success (MAC ${mac}). Click “New MAC” then “Start new test” to rerun.`;
          }
        } catch (_) {
          // ignore
        }
      });
    }
  }

export default {
  mount,
  loadAPSessions,
  loadGraceSessions,
  loadAuthmonStats,
  loadMobileSessions,
  init: () => { resolveDom(); loadMobileSessions(); },
};
